import { useCallback, useEffect, useRef, useState } from "react";
import useWebSocket, { ReadyState } from 'react-use-websocket';
import axios from "axios";
import { getUserRewards, postUserAction, postUserClaim } from "./fireblocks-ncw-client";
import { AWSWSAPIGATEWAY, AWSAPIGATEWAY } from "./constants";

import { Web3DevHubContext } from "./context";
import Web3DevHubService from "./web3DevHubService";
import { useUserProfile } from "../user/useProfileProfile";
import { getOrCreateDeviceId } from "./deviceId";

const DEFAULT_STATE = {
    logs: [],
    isProcessing: false,
    error: null,
    setup: false,
    walletStatus: {
        initialized: false,
        keysGenerated: false,
        walletCreated: false
    }
};

export const Web3DevHubProvider = ({
    children
}) => {
    const [timelockedError, setTimelockedError] = useState(null);
    const { userProfile, updateProfile } = useUserProfile();

    const [state, setState] = useState(DEFAULT_STATE);
    const session_id = 'devhubStateReward';

    const devhubService = useRef(null);

    const addLog = useCallback((message, level = 'info') => {
        const timestamp = new Date().toISOString();
        const logEntry = { timestamp, message, level };

        setState(prev => ({
            ...prev,
            logs: [...prev.logs.slice(-99), logEntry]
        }));

        if (level === 'error') {
            console.error(`[W3DH Error] ${JSON.stringify(message)}`);
        } else {
            console.log(`[W3DH::${JSON.stringify(message)}]`);
        }

        return logEntry;
    }, []);

    const updateState = useCallback((newState) => {
        setState(prev => {
            const updatedState = { ...prev, ...newState };
            return updatedState;
        });
    }, []);

    // Begin Websockets
    const [socketUrl, setSocketUrl] = useState(null);
    const [messageHistory, setMessageHistory] = useState([]);

    const {
        // sendMessage,
        // sendJsonMessage,
        lastMessage,
        lastJsonMessage,
        readyState,
        // getWebSocket,
    } = useWebSocket(socketUrl, {
        onOpen: (event) => addLog(event),
        shouldReconnect: () => true,

        // Handle User Reward claims with Websockets message filtering

        filter: async (result) => {
            // If a valid reward claim comes in, update web3devhub claim
            console.log("[W3DH:: WS: ");
            console.log(result);
            const message = JSON.parse(result.data)
            if (message.type === "code") {
                const { rewardId } = message.data;
                
                const pastRewards = JSON.parse(sessionStorage.getItem(session_id));
                if (pastRewards) {
                    addLog(pastRewards.rewardClaimed);
                    addLog(Object.prototype.hasOwnProperty.call(pastRewards.rewardClaimed, rewardId));
                    if (!Object.prototype.hasOwnProperty.call(pastRewards.rewardClaimed, rewardId)) {
                        sessionStorage.setItem(session_id, JSON.stringify({
                            "rewardClaimed": {
                                ...pastRewards?.rewardClaimed,
                                [rewardId]: 'initiated'
                            }
                        }));
                    } else {
                        if (pastRewards.rewardClaimed[rewardId]) {
                            return
                        }
                    }
                } else {
                    sessionStorage.setItem(session_id, JSON.stringify({ "rewardClaimed": { [rewardId]: 'initiated' } }));
                }
                const complete = await postUserAction(rewardId);
                sessionStorage.setItem(session_id, JSON.stringify({ "rewardClaimed": { 
                    ...pastRewards?.rewardClaimed,
                    [rewardId]: 'pending'
                } }));
                if (complete.success) {
                    const userRewards = await getUserRewards();
                    console.log("User Rewards: ", userRewards);
                    const items = userRewards.data.items;
                    console.log("Reward Items: ", items);
                    try {
                        items.forEach(async (reward) => {
                            if (reward.claimStatus === 'UNCLAIMED') {
                                console.log("Unclaimed Reward", reward);
                                await postUserClaim(reward.id);
                            };
                        });
                        sessionStorage.setItem(session_id, JSON.stringify({
                            "rewardClaimed": {
                                ...pastRewards?.rewardClaimed,
                                [rewardId]: true
                            }
                        }));
                    } catch (error) {
                        console.error("An error occurred: ", error.message);
                    }
                    window.nbc.identity.analyticsEventLogger.setMParticleUserAttribute("epicgo.claim_code", rewardId);
                    // if (!hasCollectedMedallion(rewardId)) {
                    //     // Add rewardId to collection
                    //     addMedallionToCollection(rewardId);
                    //     localStorage.setItem("pendingCongratulations", "true");
                    // }
                };
            };
            if (message.type === "error") {
                console.error(`An error occured: ${message.data}`);
            };
        },

    });

    useEffect(() => {
        if (lastMessage !== null) {
            setMessageHistory((prev) => prev.concat(lastMessage));
        }
        if (lastJsonMessage) {
            addLog(lastJsonMessage);
        }
    }, [lastMessage, lastJsonMessage]);

    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Connected',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    const getUser = useCallback(async (userId) => {
        const userResponse = await axios.get(`${AWSAPIGATEWAY}/users/${userId}`);
        const { data } = userResponse;
        if (data.deviceId) {
            addLog("User has an assigned device: " + data.deviceId);
            updateProfile({ deviceId: data.deviceId });
        }
        if (data.collection) {
            // addLog("User has collected previous rewards: " + JSON.stringify(data.collection));
            // Object.keys(data.collection).forEach((reward) => {
            //     const medallionCode = codes.medallions[Object.keys(codes.medallions).find(key => codes.nfc[key] === String(reward))];
            //     if (medallionCode) {
            //         const medallion = getMedallionByCode(medallionCode);
            //         addLog("User Medallion: " + JSON.stringify(medallion))
            //         if (medallion) {
            //             if (medallion !== null || medallion !== undefined) {
            //                 addMedallionToCollection(medallion);
            //             }
            //         }
            //     }
            // });
        }
    }, [updateProfile, addLog]);

    useEffect(() => {
        if (userProfile.userId && socketUrl === null) {
            addLog("Connecting to WS with userId: " + userProfile.userId + " And AWS API Gateway: " + AWSWSAPIGATEWAY);
            setSocketUrl(AWSWSAPIGATEWAY + "?userId=" + userProfile.userId);
            getUser(userProfile.userId);
        }
        if (userProfile.userId && !userProfile.barcode) {
            getUser(userProfile.userId);
        }
    }, [userProfile.userId, socketUrl, getUser, userProfile.barcode]);


    // End Websockets

    const startUserClaim = useCallback(async (rewardId) => {
        const pastRewards = JSON.parse(sessionStorage.getItem(session_id));
        const userRewards = await getUserRewards();
        const items = userRewards.data.items;
        addLog("Reward Items: ", items);
        try {
            items.forEach(async (reward) => {
                if (reward.claimStatus === 'UNCLAIMED') {
                    addLog("Unclaimed Reward", reward);
                    addLog("RewardId: ", reward.id);
                    await postUserClaim(reward.id);
                };
            });
            sessionStorage.setItem(session_id, JSON.stringify({
                "rewardClaimed": {
                    ...pastRewards?.rewardClaimed,
                    [rewardId]: true
                }
            }));

        } catch (error) {
            console.error("An error occurred: ", error.message);
        }
    }, [addLog]);

    const checkUserClaims = useCallback(async () => {
        const rewards = JSON.parse(sessionStorage.getItem(session_id));
        if (rewards && Object.keys(rewards.rewardClaimed).length > 0) {
            Object.keys(rewards.rewardClaimed).forEach(async(claim) => {
                if (rewards.rewardClaimed[claim] === 'pending' || rewards.rewardClaimed[claim] === 'initiated') {
                    await startUserClaim(claim);
                }
            })
        }
    }, [startUserClaim]);

    const updateUserAction = useCallback(async (rewardId) => {
        const pastRewards = JSON.parse(sessionStorage.getItem(session_id));
        const response = await postUserAction(rewardId);
        sessionStorage.setItem(session_id, JSON.stringify({
            "rewardClaimed": {
                ...pastRewards?.rewardClaimed,
                [rewardId]: 'pending'
            }
        }));
        return response;
    }, []);

    const startWalletCeremony = useCallback(async () => {

        const keys = await devhubService.current.checkKeys();

        if (!keys) {
            addLog("No keys found, starting wallet creation flow");
            const walletResult = await devhubService.current.createWalletFlow();
            if (walletResult) {
                addLog("Wallet created successfully!", 'success');
                updateState({
                    walletStatus: {
                        initialized: true,
                        keysGenerated: true,
                        walletCreated: false
                    },
                    isProcessing: false
                });
                window.nbc.identity.analyticsEventLogger.setMParticleUserAttribute("epicgo.new_user_device", window.nbc.identity.profile._id);
            } else {
                throw new Error('Wallet creation failed!');
            }
        } else {
            addLog('Existing keys found', 'info');
            updateState({
                walletStatus: {
                    initialized: true,
                    keysGenerated: true,
                    walletCreated: false
                },
                isProcessing: true
            });
        }

        const walletGenerated = await devhubService.current.checkWallet();
        if (!walletGenerated) {
            await devhubService.current.generateWallet();
            addLog('Wallet generated', 'success');
        }
        updateState({
            walletStatus: {
                initialized: true,
                keysGenerated: true,
                walletCreated: true
            },
            isProcessing: false
        });
    }, [addLog, updateState]);

    
    const analyzeDevice = useCallback(async (deviceId, accessToken) => {
        addLog(deviceId)
        const thisDeviceId = getOrCreateDeviceId();
        addLog(thisDeviceId)
        if (!deviceId || deviceId.length === 0) {
          const userResponse = await axios.get(`${AWSAPIGATEWAY}/users/${window.nbc.identity.profile._id}`);
          const { data } = userResponse;
          if (data.deviceId) {
              addLog("User has an assigned device: " + data.deviceId);
              updateProfile({ deviceId: data.deviceId });
              return data.deviceId;
          }
          return thisDeviceId;
      }
        if (deviceId.length > 0 && deviceId !== thisDeviceId) {
            // New device for existing user
            addLog("User device ID's do not match! Attempting recovery process.");
            // Initiate service with new device id
            devhubService.current = new Web3DevHubService(deviceId, accessToken);
            await devhubService.current.initialize();
            // Join to the existing wallet
            await devhubService.current.joinExistingWalletFlow();
            return deviceId;
        }
        if (deviceId.length > 0 && deviceId === thisDeviceId) {
            // Existing user on existing device
            return deviceId;
        }
    }, []);

    const refreshDevHub = (device, accessToken) => {
        addLog("Reinitializing DevHub instance.")
        devhubService.current = new Web3DevHubService(device, accessToken);
    }

    const initializeService = useCallback(async (deviceId, accessToken) => {
        addLog('Initializing W3DH service');
        updateState({ isProcessing: true });

        try {
            if (!devhubService.current) {
                addLog("Creating new DevHub instance.")
                devhubService.current = new Web3DevHubService(deviceId, accessToken);
            }
            addLog("Initializing SDK.");
            const initResult = await devhubService.current.initialize();
            if (!initResult) {
                throw new Error("Service initialization failed!");
            }

            updateState({
                walletStatus: {
                    initialized: true,
                    ...state.walletStatus,
                }
            });

            addLog("Starting wallet ceremony");
            await startWalletCeremony();

            const devhubuser = await devhubService.current.fetchUser();
            updateState({ devhubuser });
            console.log("[W3DH:: Retrieved User: "+devhubuser);
            await axios.post(`${AWSAPIGATEWAY}/users/${userProfile.userId}`,
                {
                    deviceId: deviceId,
                    dhUserId: devhubuser.userId,
                    walletAddr: devhubuser.address
                },
                {
                    headers: {
                        'Authorization': `${window.nbc.identity.token}`
                    }
                }
            ).catch(function (error) {
                addLog("An error occured sending deviceId"+ error, 'error');
            });

            if (devhubuser && devhubuser.address) {
                addLog("Checking User Rewards");
                const pastRewards = JSON.parse(sessionStorage.getItem(session_id));
                console.log("[W3DH:: Past Rewards" + JSON.stringify(pastRewards));
                if (pastRewards && pastRewards.rewardClaimed && Object.keys(pastRewards.rewardClaimed).length > 0) {
                    Object.keys(pastRewards.rewardClaimed).forEach(async (rewardId) => {
                        let keyObj = pastRewards.rewardClaimed[rewardId];
                        if (keyObj === 'initiated') {
                            await updateUserAction(rewardId);
                        }
                        if (keyObj === 'pending') {
                            await startUserClaim(rewardId);
                        }
                    });
                }
            };

        } catch (error) {
            addLog(`Initialization error: ${error.message}`, 'error');
            updateState({
                error: error.message,
                isProcessing: false
            });
            return false;
        };
        return true;
    }, [
        addLog,
        updateState,
        state.walletStatus,
        startWalletCeremony,
        session_id,
        updateUserAction,
        startUserClaim,
        userProfile.userId
    ]);

    async function sendClaimEvent(code) {
        addLog(`User Profile: ${userProfile}`);
        addLog(`Barcode ID: ${userProfile.barcode}`);
        
        const response = await axios.post(
            `${AWSAPIGATEWAY}/codes/${code}/redeem`,
        {
            headers: {
                'Authorization': `Bearer ${window.nbc.identity.token}`
              }
        });
        addLog(response);
        return response;
    }

    const prepareService = async (deviceId, accessToken) => {
        setState({
            ...state,
            isProcessing: true
        })
        let device = await analyzeDevice(deviceId, accessToken);
        if (!devhubService.current) {
            addLog("Creating new DevHub instance.");
            devhubService.current = new Web3DevHubService(device, accessToken);
        };
        const devhubuser = await devhubService.current.fetchUser();
        if (devhubuser && devhubuser.address) {
            addLog(`Existing Web3 User Found: ${JSON.stringify(devhubuser)}`);
            await axios.post(`${AWSAPIGATEWAY}/users/${window.nbc.identity.profile._id}`,
                {
                    deviceId: device,
                    walletAddr: devhubuser.address
                },
                {
                    headers: {
                        'Authorization': `${window.nbc.identity.token}`
                    }
                }
            ).catch(function (error) {
                addLog("An error occured sending deviceId "+ error, 'error');
            });
            return
        } else {
            await initializeService(device, accessToken);
        }
    }

    useEffect(() => {
        if (!devhubService.current) {
            return;
        };
        refreshDevHub(userProfile.deviceId, window.nbc.identity.token);

    }, [window.nbc.identity.token]);

    useEffect(() => {

        if (!window.nbc.identity || !userProfile.userId || userProfile.userId === undefined || userProfile.userId === null) {
            return;
        };
        
        if (!window.nbc.identity.token) {
            return;
        };

        const accessToken = window.nbc.identity.token;

        if (devhubService.current) {
            return;
        };
        
        let isMounted = true;

        addLog("Initializing W3DH provider!");

        const initialize = async () => {
            if (isMounted && !state.isProcessing) {
                await prepareService(userProfile.deviceId, accessToken);
            }
        }

        initialize();

        const intervalId = setInterval(() => {
            checkUserClaims();
        }, 30000);

        return () => {
            isMounted = false
            addLog('DevHub provider unmounting', 'info');
            clearInterval(intervalId);
        }

    }, [
        userProfile.deviceId, 
        userProfile.userId, 
        window.nbc.identity.token, 
        addLog]);

    const contextValue = {

        service: devhubService.current,
        w3dhState: state,
        wsConnectionStatus: connectionStatus,
        devHubUser: devhubService.current?.user ?? null,
        keyStatus: state.walletStatus.initialized &&
            state.walletStatus.keysGenerated &&
            state.walletStatus.walletCreated,
        startUserClaim: startUserClaim,
        updateUserAction: updateUserAction,
        getUser: getUser,
        sendClaimEvent: sendClaimEvent,
        wsMessageHistory: messageHistory,
        refreshDevHub: refreshDevHub,
        timelockedError,
        setTimelockedError
    };

    return (
        <Web3DevHubContext.Provider value={contextValue}>
            {children}
        </Web3DevHubContext.Provider>
    )
}